import { ApolloError } from '@apollo/client';
import { SpaceBetween } from '@risk-smart/themed-cloudscape-components';
import { Container, RiskScoringSettings } from '@risksmart-app/atomic-ui';
import Button from '@risksmart-app/components/src/button';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useInsertRiskAssessmentResultConfig,
  useUpdateRiskAssessmentResultConfig,
} from 'src/hooks/mutations';
import { PageLayout } from 'src/layouts';

import { ConflictDialog } from './dialogs/ConflictDialog';
import { DiscardDialog } from './dialogs/DiscardDialog';
import { SaveDialog } from './dialogs/SaveDialog';
import { buildLang } from './lang';
import { transformToApiConfig } from './transform';
import { useRiskScoringSettingsStore } from './useRiskScoringSettingsStore';

const isConflictError = (error: unknown): boolean =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(
    (e) => e.extensions?.code === 'modified-since-last-view'
  );

const Page = () => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'riskScoringSettings',
  });
  const {
    state,
    actions,
    isLoading,
    id,
    originalTimestamp,
    resetToInitial,
    refetch,
  } = useRiskScoringSettingsStore();
  const { mutate: updateConfig } = useUpdateRiskAssessmentResultConfig();
  const { mutate: insertConfig } = useInsertRiskAssessmentResultConfig();
  const { addNotification } = useNotifications();

  const isSettingsComplete =
    state.isImpactLikelihoodComplete &&
    (state.selectedMethodology === 'multi-impact'
      ? state.isMultiImpactComplete
      : true);

  const requiresNewVersion = state.changeStatus === 'structural';
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async (mode: 'update' | 'insert') => {
    setIsSaving(true);

    try {
      const config = transformToApiConfig({
        likelihoodLevels: state.likelihoodLevels,
        impactLevels: state.impactLevels,
        impactCategories: state.impactCategories,
        impactAggregation: state.impactAggregation,
        matrix: state.matrix,
      });

      if (mode === 'update') {
        await updateConfig({
          variables: {
            Id: id!,
            Config: config,
            OriginalTimestamp: originalTimestamp!,
          },
        });
      } else {
        await insertConfig({
          variables: { Config: config },
        });
      }

      setShowSaveDialog(false);
      addNotification({
        type: 'success',
        content: t(
          mode === 'update'
            ? 'update.successMessage'
            : 'saveAsNew.successMessage'
        ),
      });
      await refetch();
    } catch (error: unknown) {
      setShowSaveDialog(false);

      if (mode === 'update' && isConflictError(error)) {
        setShowConflictDialog(true);
      } else {
        addNotification({
          type: 'error',
          content: t(
            mode === 'update' ? 'update.errorMessage' : 'saveAsNew.errorMessage'
          ),
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setShowDiscardDialog(false);
    resetToInitial();
    addNotification({
      type: 'success',
      content: t('discard.successMessage'),
    });
  };

  const handleReload = async () => {
    setShowConflictDialog(false);
    await refetch();
    resetToInitial();
  };

  const lang = buildLang(t);

  if (isLoading) {
    return <PageLayout title={t('page.title')} />;
  }

  return (
    <PageLayout
      title={t('page.title')}
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            variant={'primary'}
            formAction={'none'}
            onClick={() => setShowSaveDialog(true)}
            disabled={
              state.changeStatus === 'none' || isLoading || !isSettingsComplete
            }
          >
            {t('save.button')}
          </Button>
          <Button
            variant={'normal'}
            formAction={'none'}
            onClick={() => setShowDiscardDialog(true)}
            disabled={state.changeStatus === 'none' || isLoading}
          >
            {t('discard.button')}
          </Button>
        </SpaceBetween>
      }
    >
      <Container className={'mx-auto'}>
        <RiskScoringSettings lang={lang} state={state} actions={actions} />
      </Container>

      <SaveDialog
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        onUpdate={() => handleSave('update')}
        onInsert={() => handleSave('insert')}
        isSaving={isSaving}
        requiresNewVersion={requiresNewVersion}
      />
      <ConflictDialog
        open={showConflictDialog}
        onOpenChange={setShowConflictDialog}
        onReload={handleReload}
      />
      <DiscardDialog
        open={showDiscardDialog}
        onOpenChange={setShowDiscardDialog}
        onDiscard={handleDiscard}
      />
    </PageLayout>
  );
};

export default Page;
